import api, {
    ClientLoanProgram,
    CreateLoanProgramReqBody,
    EligibilityGuideline,
    EligibilityGuidelineSet,
    EligibilityGuidelineSetSummary,
    LoanProperty
} from '@api';
import { AccountBalance, AddCircleOutline } from '@mui/icons-material';
import {
    Button, DialogContent, Divider, FormHelperText, MenuItem, Paper, Step, StepLabel, Stepper, Typography
} from '@mui/material';
import {
    DateField, DialogActions, OptionalWrapper, PaperSaveLoader, Switch, TextField
} from '@tsp-ui/core/components';
import utilStyles from '@tsp-ui/core/sass/style-utils.module.scss';
import {
    ConvertValues,
    DeepPartial,
    booleanObjectsToEnums,
    enumToBoolean,
    getItemById,
    isFutureDate,
    isPastDate,
    isToday,
    replaceItemById,
    tryGetItemById,
    useAsyncEffect,
    usePageMessage,
    useParams
} from '@tsp-ui/core/utils';
import { AdminSubRouteParams } from '@views/admin/components/AdminPageTemplate';
import { InvestorEligibilityContext } from '@views/admin/investor-eligibility/InvestorEligibilityRoutes';
import { SelectableButton } from '@views/admin/investor-eligibility/components/ClientInvestorForm';
import { LoanProgramsContext } from '@views/admin/investors/InternalInvestorDetailRoutes';
import {
    EligibilityExclusionsFormValues,
    ExclusionGroupRow,
    exclusionsToFormValues,
    formValuesToExclusions,
    getEmptyEligibilityExclusions
} from '@views/admin/investors/InvestorDetailPage/components/ExclusionGroupRow';
import clsx from 'clsx';
import {
    Dispatch, SetStateAction, useCallback, useContext, useState
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { EligibilityGuidelineGrid } from '../../components/EligibilityGuidelineGrid';
import { ProgramEligibilityContext } from '../InternalLoanProgramDetailPage';

import { EligibilityGuidelinesContext } from './EditEligibilityGuidelinesDialog';
import { EditHighLevelGuidelines } from './HighLevelGuidelines';
import { LoanProgramDetailsFields } from './LoanProgramDetailsFields';
import styles from './LoanProgramForm.module.scss';


export type EligibilityGuidelineFormValues = ConvertValues<EligibilityGuideline, boolean | undefined, 'TRUE' | 'FALSE'>;

export interface LoanProgramFormValues extends Omit<CreateLoanProgramReqBody, 'eligibilityGuidelineSet'> {
    eligibilityGuidelineSet: Omit<Required<CreateLoanProgramReqBody>['eligibilityGuidelineSet'], 'guidelines' | 'exclusions'> & {
        guidelines: EligibilityGuidelineFormValues[];
        exclusions: EligibilityExclusionsFormValues;
    };
    copyExistingMatrix?: boolean;
    createNew?: boolean;
}

interface LoanProgramFormProps {
    isEdit?: boolean;
    activeStep: number;
    setActiveStep: Dispatch<SetStateAction<number>>;
    defaultValues?: DeepPartial<LoanProgramFormValues>;
}

export function LoanProgramForm({
    isEdit = false, activeStep, setActiveStep, defaultValues
}: LoanProgramFormProps) {
    const { loanPrograms = [], setLoanPrograms } = useContext(LoanProgramsContext);
    const { eligibilityGuidelineSets = [], setEligibilityGuidelineSets } = useContext(ProgramEligibilityContext);
    const { eligibilityGuidelineSet, setEligibilityGuidelineSet } = useContext(EligibilityGuidelinesContext);

    const { pathname } = useLocation();
    const isClient = pathname.includes('investor-eligibility');

    const navigate = useNavigate();
    const pageMessage = usePageMessage();
    const { investorID, loanProgramID, guidelineSetID } = useParams<AdminSubRouteParams<'guidelineSet'>>();

    const [ loading, setLoading ] = useState(false);

    const formMethods = useForm<LoanProgramFormValues>({
        defaultValues: defaultValues || {
            eligibilityGuidelineSet: {
                exclusions: getEmptyEligibilityExclusions()
            }
        }
    });

    const createNew = formMethods.watch('createNew');
    const baseLoanProgramId = formMethods.watch('loanProgram.baseLoanProgramId');
    const copyExistingMatrix = formMethods.watch('copyExistingMatrix');
    const guidelinesValues = formMethods.watch('eligibilityGuidelineSet.guidelines');

    const { investors } = useContext(InvestorEligibilityContext);

    const investor = getItemById(investors || [], investorID);
    const baseInvestor = tryGetItemById(investors || [], investor?.eligibilityBasedOnInvestorId || '');
    const baseInvestorID = baseInvestor?.id;

    const [ baseLoanPrograms, setBaseLoanPrograms ] = useState<ClientLoanProgram[]>([]);

    useAsyncEffect(useCallback(async () => {
        if (baseInvestorID) {
            try {
                setBaseLoanPrograms(await api.client.investors.getLoanPrograms(baseInvestorID));
            } catch (error) {
                pageMessage.handleApiError('An error occurred while fetching base loan programs.', error);
            }
        }
    }, [ baseInvestorID, pageMessage ]));

    const handleSubmit = formMethods.handleSubmit(async formValues => {
        const formData = convertFormValues(formValues);

        if (activeStep === 0) {
            try {
                if (copyExistingMatrix) {
                    let guidelineSet = eligibilityGuidelineSet;

                    if (!guidelineSet) {
                        setLoading(true);

                        guidelineSet = await api.investors.getEligibilityGuidelineSetDetail(
                            investorID, loanProgramID, guidelineSetID || eligibilityGuidelineSets.find(
                                guidelineSet => !guidelineSet.isOverlay
                            )!.id
                        );

                        setEligibilityGuidelineSet(guidelineSet);

                        setLoading(false);
                    }

                    formMethods.setValue('eligibilityGuidelineSet.exclusions', exclusionsToFormValues(guidelineSet.exclusions));
                    formMethods.setValue('eligibilityGuidelineSet.highLevelGuidelines', booleanObjectsToEnums(guidelineSet.highLevelGuidelines));
                    formMethods.setValue('eligibilityGuidelineSet.guidelines', booleanObjectsToEnums(guidelineSet.guidelines));
                } else if (guidelinesValues?.length) {
                    // remove the guidelines if user goes back and unchecks copyExistingMatrix
                    formMethods.setValue('eligibilityGuidelineSet.exclusions', getEmptyEligibilityExclusions());
                    formMethods.setValue('eligibilityGuidelineSet.highLevelGuidelines', []);
                    formMethods.setValue('eligibilityGuidelineSet.guidelines', []);
                }
            } catch (error) {
                pageMessage.handleApiError('An error occurred while fetching guideline set details', error);
            }

            if (isClient && baseLoanProgramId) {
                // this is a CLIENT activating an inherited loan program
                const { loanProgram } = await api.investors.createLoanProgram({
                    loanProgram: {
                        ...getItemById(baseLoanPrograms, baseLoanProgramId)
                    }
                });

                setLoanPrograms([ ...loanPrograms, loanProgram ]);

                navigate(`../loan-programs/${loanProgram.id}`);

                pageMessage.success('Loan program activated');
            }
        }

        if (activeStep < 2) {
            setActiveStep(activeStep + 1);
        } else {
            setLoading(true);

            try {
                if (isEdit) {
                    if (defaultValues && isFutureDate(defaultValues.eligibilityGuidelineSet?.effectiveDate)) {
                        // if this is an edit to PENDING guidelines
                        const updatedGuidelineSet = await api.investors.updateEligibilityGuidelineSet(
                                formData.eligibilityGuidelineSet as EligibilityGuidelineSet, investorID
                        );

                        setEligibilityGuidelineSets(replaceItemById(eligibilityGuidelineSets, updatedGuidelineSet));
                    } else {
                        // if this is an edit to active guidelines, cloning of expired guidelines or creation of overlay
                        const newGuidelineSet = await api.investors.createEligibilityGuidelineSet({
                            ...formData.eligibilityGuidelineSet!,
                            loanProgramId: loanProgramID,
                            isOverlay: pathname.includes('investor-eligibility') && !!loanPrograms.find(({ id }) => id === loanProgramID)?.baseLoanProgramId
                        }, investorID);

                        let updatedGuidelineSets = [ ...eligibilityGuidelineSets, newGuidelineSet ];

                        // if the new guideline is set to be active, expire the old one by updating the expirationDate
                        if (guidelineSetIsActive(formData.eligibilityGuidelineSet)) {
                            const activeGuidelineSet = getActiveGuidelineSet(eligibilityGuidelineSets);

                            if (activeGuidelineSet) {
                                updatedGuidelineSets = replaceItemById(updatedGuidelineSets, {
                                    ...activeGuidelineSet,
                                    expirationDate: new Date().toISOString()
                                });
                            }
                        }

                        setEligibilityGuidelineSets(updatedGuidelineSets);
                    }

                    navigate('..');

                    pageMessage.success('Eligibility guidelines saved');
                } else {
                    // if this is a creation of new loanProgram
                    const { loanProgram, eligibilityGuidelineSet } = await api.investors.createLoanProgram({
                        ...formData,
                        eligibilityGuidelineSet: {
                            ...formData.eligibilityGuidelineSet!,
                            guidelines: formData.eligibilityGuidelineSet!.guidelines.map(
                                guideline => enumToBoolean(guideline, [ 'highBalance' ])
                            )
                        }
                    });

                    setLoanPrograms([ ...loanPrograms, loanProgram ]);

                    const updatedEligibilityGuidelineSets = [ ...eligibilityGuidelineSets, eligibilityGuidelineSet! ];

                    setEligibilityGuidelineSets(updatedEligibilityGuidelineSets);

                    navigate(`../loan-programs/${loanProgram.id}`, { state: { eligibilityGuidelineSets: updatedEligibilityGuidelineSets } });

                    pageMessage.success('Loan program created');
                }
            } catch (error) {
                pageMessage.handleApiError('An error occurred while creating the loan program', error);
            }

            setLoading(false);
        }
    });

    return (
        <>
            {(!isClient || createNew || isEdit) && (
                <OptionalWrapper
                    Component={DialogContent}
                    renderWrapper={isEdit}
                    className={styles.stepperContent}
                >
                    <Stepper
                        activeStep={activeStep}
                        className={clsx(styles.stepper, {
                            [styles.matrixStepper]: activeStep === 1 && isEdit,
                            [styles.pageStepper]: !isEdit
                        })}
                    >
                        <Step>
                            <StepLabel>
                                Details
                            </StepLabel>
                        </Step>

                        <Step>
                            <StepLabel>
                                Matrix
                            </StepLabel>
                        </Step>

                        <Step>
                            <StepLabel>
                                Exclusions
                            </StepLabel>
                        </Step>
                    </Stepper>
                </OptionalWrapper>
            )}

            <OptionalWrapper
                Component={DialogContent}
                renderWrapper={isEdit}
                className={clsx(styles.content, { [styles.matrixContent]: activeStep === 1 })}
            >
                <form
                    id="loan-program-form"
                    onSubmit={handleSubmit}
                    noValidate
                    className={clsx({
                        [styles.fullWidth]: activeStep === 1,
                        [styles.matrixFormRoot]: activeStep === 1
                    })}
                >
                    <FormProvider {...formMethods}>
                        {activeStep === 0 ? (
                            <OptionalWrapper
                                Component={Paper}
                                renderWrapper={!isEdit}
                            >
                                <div
                                    className={clsx(styles.detailsFields, {
                                        [styles.createDetails]: !isEdit
                                    })}
                                >
                                    {(!isEdit && isClient && investor?.eligibilityBasedOnInvestorId) && (
                                        <>
                                            <Typography
                                                align="center"
                                                className={utilStyles.fullWidth}
                                            >
                                                {`${investor?.name} has eligibility based on ${baseInvestor?.name}`}
                                            </Typography>

                                            <SelectableButton
                                                selected={createNew === false}
                                                onClick={() => formMethods.setValue('createNew', false)}
                                                className={styles.inheritOrCreateButton}
                                            >
                                                <AccountBalance
                                                    color="secondary"
                                                    className={styles.icon}
                                                />
                                                Activate Inherited Program
                                            </SelectableButton>

                                            <SelectableButton
                                                selected={createNew === true}
                                                onClick={() => formMethods.setValue('createNew', true)}
                                                className={styles.inheritOrCreateButton}
                                            >
                                                <AddCircleOutline
                                                    color="secondary"
                                                    className={styles.icon}
                                                />

                                                Create New Program
                                            </SelectableButton>

                                            {createNew !== undefined && (
                                                <Divider className={clsx(utilStyles.fullWidth, styles.divider)} />
                                            )}

                                            {createNew === false && (
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        align="center"
                                                    >
                                                        Select a loan program to inherit eligibility from:
                                                    </Typography>

                                                    <TextField<LoanProgramFormValues>
                                                        name="loanProgram.baseLoanProgramId"
                                                        select
                                                        SelectProps={{
                                                            classes: { select: styles.select }
                                                        }}
                                                        size="small"
                                                        required
                                                    >
                                                        {baseLoanPrograms.filter(
                                                            ({ hasActiveGuidelineSet }) => hasActiveGuidelineSet
                                                        ).map(({ name, id }) => (
                                                            <MenuItem
                                                                value={id}
                                                                key={id}
                                                            >
                                                                {name}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </>
                                            )}
                                        </>

                                    )}

                                    {(
                                        (
                                            !isClient && !isEdit
                                        ) || (
                                            isClient && (createNew || !investor?.eligibilityBasedOnInvestorId)
                                        )
                                    ) && <LoanProgramDetailsFields />}

                                    {(!isClient || (
                                        isClient && (createNew || !investor?.eligibilityBasedOnInvestorId)
                                    ) || isEdit) && (
                                        <>
                                            <DateField<LoanProgramFormValues>
                                                label="Effective date"
                                                name="eligibilityGuidelineSet.effectiveDate"
                                                helperText="The date that these edits take effect"
                                                required
                                            />

                                            <DateField<LoanProgramFormValues>
                                                label="Expiration date"
                                                name="eligibilityGuidelineSet.expirationDate"
                                                helperText="The date that these edits are set to expire"
                                            />
                                        </>
                                    )}

                                    {isEdit && (
                                        <>
                                            <TextField<LoanProgramFormValues>
                                                name="eligibilityGuidelineSet.comments"
                                                label="Comments"
                                                multiline
                                                rows={2}
                                            />

                                            <div>
                                                <Switch<LoanProgramFormValues>
                                                    name="copyExistingMatrix"
                                                    label="Copy existing matrix"
                                                />

                                                <FormHelperText>
                                                    The eligibility matrix in the next step will be

                                                    {copyExistingMatrix
                                                        ? ' pre-populated with the existing values'
                                                        : ' empty'}
                                                </FormHelperText>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </OptionalWrapper>
                        ) : activeStep === 1 ? (
                            <div className={styles.gridContainer}>
                                <EditHighLevelGuidelines name="eligibilityGuidelineSet.highLevelGuidelines" />

                                <div className={styles.innerGridContainer}>
                                    <EligibilityGuidelineGrid />
                                </div>
                            </div>
                        ) : (
                            <Paper
                                variant="outlined"
                                className={styles.filterContainer}
                            >
                                <ExclusionGroupRow nameBase="eligibilityGuidelineSet.exclusions" />
                            </Paper>
                        )}
                    </FormProvider>
                </form>
            </OptionalWrapper>

            <OptionalWrapper
                Component={DialogActions}
                renderWrapper={isEdit}
                className={styles.dialogActions}
            >
                <div className={styles.buttonContainer}>
                    {activeStep > 0 ? (
                        <Button
                            onClick={() => setActiveStep(activeStep - 1)}
                        >
                            Back
                        </Button>
                    ) : (
                        <Button
                            component={Link}
                            to=".."
                        >
                            Cancel
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        type="submit"
                        form="loan-program-form"
                        disabled={isClient && createNew === undefined && !isEdit}
                    >
                        {(activeStep === 2 || (isClient && !createNew && !isEdit)) ? 'Submit' : 'Next'}
                    </Button>

                </div>
            </OptionalWrapper>

            <PaperSaveLoader loading={loading} />
        </>
    );
}

function convertFormValues(
    { copyExistingMatrix, createNew, ...formValues }: LoanProgramFormValues
): CreateLoanProgramReqBody {
    return {
        ...formValues,
        eligibilityGuidelineSet: {
            ...formValues.eligibilityGuidelineSet,
            highLevelGuidelines: formValues.eligibilityGuidelineSet.highLevelGuidelines?.map(highLevelGuideline => {
                if (highLevelGuideline.loanProperty === LoanProperty.HIGH_BALANCE) {
                    return enumToBoolean(highLevelGuideline, [ 'value' ]);
                } else {
                    return highLevelGuideline;
                }
            }),
            guidelines: formValues.eligibilityGuidelineSet.guidelines?.map(
                guideline => enumToBoolean(guideline, [ 'highBalance' ])
            ),
            exclusions: formValuesToExclusions(formValues.eligibilityGuidelineSet.exclusions)
        }
    };
}

export function getActiveGuidelineSet(eligibilityGuidelineSets?: EligibilityGuidelineSetSummary[]) {
    return eligibilityGuidelineSets?.find(guidelineSet => guidelineSetIsActive(guidelineSet));
}

type GuidelineSetFormValues = CreateLoanProgramReqBody['eligibilityGuidelineSet'];
export function guidelineSetIsActive(
    guidelineSet: EligibilityGuidelineSet | EligibilityGuidelineSetSummary | GuidelineSetFormValues
) {
    return guidelineSet && (
        isPastDate(guidelineSet.effectiveDate) || isToday(guidelineSet.effectiveDate)
    ) && (
        !guidelineSet.expirationDate || isFutureDate(guidelineSet.expirationDate)
    );
}
